import {
  affectsPullRequestList,
  affectsPullRequestStatistics,
  subscribePullRequestInvalidation,
} from "../pullRequestInvalidation";
import { useAtomValue } from "@effect/atom-react";
import { createPullRequestEnvironmentAtoms } from "@afar-research/client-runtime/state/pull-requests";
import type {
  EnvironmentId,
  PullRequestListInput,
  PullRequestListItem,
  ProjectId,
  PullRequestListResult,
  PullRequestListStatsInput,
  PullRequestListStatsResult,
  PullRequestChecksInput,
  PullRequestChecksResult,
  PullRequestDetailInput,
  PullRequestDetailResult,
  PullRequestCodeInput,
  PullRequestCodeResult,
  PullRequestTimelineInput,
  PullRequestTimelineResult,
} from "@afar-research/contracts";
import * as Cause from "effect/Cause";
import * as Option from "effect/Option";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useCallback, useEffect, useMemo } from "react";

import { connectionAtomRuntime } from "../connection/runtime";
import { appAtomRegistry } from "../rpc/atomRegistry";
import { useEnvironmentQuery } from "./query";

export const pullRequestEnvironment = createPullRequestEnvironmentAtoms(connectionAtomRuntime);

export interface EnvironmentQueryTarget<Input> {
  readonly environmentId: EnvironmentId;
  readonly input: Input;
}

interface MergedEnvironmentQueryView<A> {
  readonly values: ReadonlyArray<readonly [EnvironmentId, A]>;
  readonly failures: ReadonlyArray<readonly [EnvironmentId, string]>;
  readonly isPending: boolean;
}

function formatFailure(cause: Cause.Cause<unknown>): string {
  const error = Cause.squash(cause);
  return error instanceof Error && error.message.trim().length > 0
    ? error.message
    : "The AFAR Environment request failed.";
}

function createMergedEnvironmentQuery<Input, A>(
  label: string,
  affected: (
    input: Input,
    changed: import("../pullRequestInvalidation").PullRequestInvalidationTarget,
  ) => boolean,
  atomFor: (
    target: EnvironmentQueryTarget<Input>,
  ) => Atom.Atom<AsyncResult.AsyncResult<A, unknown>>,
) {
  const family = Atom.family((key: string) =>
    Atom.make((get): MergedEnvironmentQueryView<A> => {
      const targets = JSON.parse(key) as ReadonlyArray<EnvironmentQueryTarget<Input>>;
      const values: Array<readonly [EnvironmentId, A]> = [];
      const failures: Array<readonly [EnvironmentId, string]> = [];
      let isPending = false;
      for (const target of targets) {
        const result = get(atomFor(target));
        isPending ||= result.waiting;
        if (result._tag === "Failure") {
          failures.push([target.environmentId, formatFailure(result.cause)]);
        }
        const value = Option.getOrNull(AsyncResult.value(result));
        if (value !== null) values.push([target.environmentId, value]);
      }
      return { values, failures, isPending };
    }).pipe(Atom.withLabel(`${label}:${key}`)),
  );
  const empty = Atom.make<MergedEnvironmentQueryView<A>>({
    values: [],
    failures: [],
    isPending: false,
  }).pipe(Atom.withLabel(`${label}:empty`));

  return function useMergedQuery(targets: ReadonlyArray<EnvironmentQueryTarget<Input>>) {
    const key = JSON.stringify(targets);
    const view = useAtomValue(targets.length === 0 ? empty : family(key));
    const refresh = useCallback(() => {
      for (const target of JSON.parse(key) as ReadonlyArray<EnvironmentQueryTarget<Input>>) {
        appAtomRegistry.refresh(atomFor(target));
      }
    }, [atomFor, key]);
    useEffect(
      () =>
        subscribePullRequestInvalidation((changed) => {
          for (const target of JSON.parse(key) as ReadonlyArray<EnvironmentQueryTarget<Input>>) {
            if (target.environmentId === changed.environmentId && affected(target.input, changed))
              appAtomRegistry.refresh(atomFor(target));
          }
        }),
      [key, atomFor],
    );
    return { ...view, refresh };
  };
}

export const usePullRequestLists = createMergedEnvironmentQuery<
  PullRequestListInput,
  PullRequestListResult
>("web-pull-requests:list", affectsPullRequestList, pullRequestEnvironment.list);

/**
 * Every environment's listing flattened into the one list a picker reads. AFAR
 * partitions a listing by repository, so the rows are the ready partitions'
 * entries; an unavailable partition contributes none and is reported through
 * the query's own failures.
 */
export interface MergedPullRequestList {
  readonly entries: ReadonlyArray<PullRequestListItem>;
  /** Projects whose listing could not be read, so a caller can say so. */
  readonly unavailableProjectIds: ReadonlyArray<ProjectId>;
}

export function usePullRequestList(
  targets: ReadonlyArray<EnvironmentQueryTarget<PullRequestListInput>>,
): {
  readonly data: MergedPullRequestList | null;
  /** The first environment that could not be asked at all, as opposed to one whose listing came back unavailable. */
  readonly error: string | null;
  readonly isPending: boolean;
  readonly refresh: (targets?: ReadonlyArray<EnvironmentQueryTarget<PullRequestListInput>>) => void;
} {
  const query = usePullRequestLists(targets);
  const data = useMemo(
    () =>
      query.values.length === 0
        ? null
        : {
            entries: query.values.flatMap(([, result]) =>
              result.partitions.flatMap((partition) =>
                partition.status === "ready" ? partition.entries : [],
              ),
            ),
            unavailableProjectIds: query.values.flatMap(([, result]) =>
              result.partitions.flatMap((partition) =>
                partition.status === "unavailable" ? [partition.projectId] : [],
              ),
            ),
          },
    [query.values],
  );
  return {
    data,
    error: query.failures[0]?.[1] ?? null,
    isPending: query.isPending,
    refresh: query.refresh,
  };
}

export const usePullRequestListStats = createMergedEnvironmentQuery<
  PullRequestListStatsInput,
  PullRequestListStatsResult
>("web-pull-requests:list-stats", affectsPullRequestStatistics, pullRequestEnvironment.listStats);

export function usePullRequestDetail(
  target: EnvironmentQueryTarget<PullRequestDetailInput> | undefined,
) {
  return useEnvironmentQuery<PullRequestDetailResult, unknown>(
    target === undefined ? null : pullRequestEnvironment.detail(target),
  );
}

export function usePullRequestChecks(
  target: EnvironmentQueryTarget<PullRequestChecksInput> | undefined,
) {
  return useEnvironmentQuery<PullRequestChecksResult, unknown>(
    target === undefined ? null : pullRequestEnvironment.checks(target),
  );
}

export function usePullRequestTimeline(
  target: EnvironmentQueryTarget<PullRequestTimelineInput> | undefined,
) {
  return useEnvironmentQuery<PullRequestTimelineResult, unknown>(
    target === undefined ? null : pullRequestEnvironment.timeline(target),
  );
}

export function usePullRequestCode(
  target: EnvironmentQueryTarget<PullRequestCodeInput> | undefined,
) {
  return useEnvironmentQuery<PullRequestCodeResult, unknown>(
    target === undefined ? null : pullRequestEnvironment.code(target),
  );
}
